import {
  useState,
  useEffect,
  useRef,
  useMemo,
  createContext,
  useContext,
  CSSProperties,
  JSX,
} from 'react';
import { useTranslation } from 'react-i18next';
import './QuantumRadar.css';

// ─── Math helpers ─────────────────────────────────────────────────────
const clamp01 = (v: number) => Math.max(0, Math.min(1, v));
const easeOut = (t: number) => 1 - Math.pow(1 - t, 2);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
function hash(n: number) {
  const s = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return s - Math.floor(s);
}

// ─── Types ────────────────────────────────────────────────────────────
type Step = { heading: string; body: string };
type Chapter = { number: string; title: string; intro: string; steps: Step[] };
type CtxValue = { progress: number; stepProgress: number[] };

const ChapterContext = createContext<CtxValue>({ progress: 0, stepProgress: [0, 0, 0] });

// ═══════════════════════════════════════════════════════════════════════
// VISUAL PRIMITIVES
// ═══════════════════════════════════════════════════════════════════════

function GridBG({ mode = 'classical' }: { mode?: 'classical' | 'quantum' }) {
  const color = mode === 'classical' ? 'rgba(230, 180, 90, 0.09)' : 'rgba(170, 150, 230, 0.11)';
  return (
    <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
      <defs>
        <pattern id={`g-${mode}`} width="48" height="48" patternUnits="userSpaceOnUse">
          <path d="M 48 0 L 0 0 0 48" fill="none" stroke={color} strokeWidth="1" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#g-${mode})`} />
    </svg>
  );
}

function Station({
  x, y, mode = 'classical', label, sub, emitGlow = 0,
}: { x: number; y: number; mode?: 'classical' | 'quantum'; label: string; sub?: string; emitGlow?: number }) {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const accent = mode === 'classical' ? 'oklch(82% 0.14 75)' : 'oklch(78% 0.14 260)';
  return (
    <div style={{
      position: 'absolute', left: x, top: y,
      transform: 'translate(-50%, -100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      pointerEvents: 'none',
    }}>
      <svg width="64" height="64" viewBox="0 0 64 64" style={{
        filter: emitGlow > 0 ? `drop-shadow(0 0 ${14 * emitGlow}px ${accent})` : 'none',
        transition: 'filter 0.2s',
      }}>
        <rect x="28" y="44" width="8" height="14" fill={accent} opacity="0.7" />
        <rect x="22" y="56" width="20" height="4" fill={accent} opacity="0.7" />
        <path d="M 12 28 Q 32 4, 52 28 L 48 32 Q 32 12, 16 32 Z" fill={accent} />
        <line x1="32" y1="28" x2="32" y2="44" stroke={accent} strokeWidth="2" />
        <circle cx="32" cy="18" r="3" fill={accent} opacity={0.5 + emitGlow * 0.5} />
      </svg>
      <div style={{
        marginTop: 8, fontFamily: 'JetBrains Mono, monospace',
        fontSize: 10, color: accent, letterSpacing: '0.15em', textTransform: 'uppercase',
      }}>{label}</div>
      {sub && <div style={{
        marginTop: 2, fontFamily: 'JetBrains Mono, monospace',
        fontSize: 9, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase',
      }}>{sub}</div>}
    </div>
  );
}

function Plane({
  x, y, stealth = false, scale = 1, glow = 0,
}: { x: number; y: number; stealth?: boolean; scale?: number; glow?: number }) {
  const body = stealth ? '#1a1c22' : '#cfd3dc';
  const edge = stealth ? '#3b3f4a' : '#e8ebf1';
  return (
    <div style={{
      position: 'absolute', left: x, top: y,
      transform: `translate(-50%, -50%) scale(${scale})`,
      filter: glow > 0 ? `drop-shadow(0 0 ${14 * glow}px rgba(180,140,255,${0.65 * glow}))` : 'none',
      pointerEvents: 'none',
    }}>
      {stealth ? (
        <svg width="180" height="88" viewBox="0 0 160 80">
          <defs>
            <linearGradient id="stealth-g" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#2a2d35" />
              <stop offset="100%" stopColor="#0e1014" />
            </linearGradient>
          </defs>
          <path d="M 80 18 L 140 52 L 130 58 L 92 50 L 88 64 L 72 64 L 68 50 L 30 58 L 20 52 Z"
            fill="url(#stealth-g)" stroke={edge} strokeWidth="0.75" />
          <path d="M 76 28 L 80 22 L 84 28 L 82 38 L 78 38 Z" fill="#4a5064" opacity="0.8" />
          <path d="M 80 18 L 92 50 L 72 50 Z" fill="#1d2028" opacity="0.5" />
        </svg>
      ) : (
        <svg width="180" height="70" viewBox="0 0 160 60">
          <ellipse cx="80" cy="30" rx="66" ry="5" fill={body} />
          <path d="M 76 28 L 96 44 L 100 44 L 84 26 Z" fill={body} />
          <path d="M 84 32 L 100 48 L 96 48 L 76 32 Z" fill={edge} opacity="0.5" />
          <path d="M 76 28 L 56 44 L 60 44 L 84 26 Z" fill={body} transform="translate(24,0)" />
          <path d="M 20 30 L 10 22 L 14 22 L 28 28 Z" fill={body} />
          <path d="M 20 30 L 10 38 L 14 38 L 28 32 Z" fill={body} />
          <rect x="36" y="28" width="60" height="1.5" fill="rgba(60,80,120,0.6)" />
          <circle cx="14" cy="30" r="2" fill="rgba(255,200,120,0.8)" />
        </svg>
      )}
    </div>
  );
}

function SignalPacket({
  fromX, fromY, toX, toY, progress, color, size = 10, label,
}: { fromX: number; fromY: number; toX: number; toY: number; progress: number; color: string; size?: number; label?: string }) {
  const p = clamp01(progress);
  if (p <= 0 || p >= 1) return null;
  if (![fromX, fromY, toX, toY].every(Number.isFinite)) return null;

  const x = lerp(fromX, toX, p);
  const y = lerp(fromY, toY, p);
  const tailCount = 8;
  const tailLen = 0.12;
  const tail: { x: number; y: number; a: number; s: number }[] = [];
  for (let i = 1; i <= tailCount; i++) {
    const tp = p - (i / tailCount) * tailLen;
    if (tp <= 0) break;
    tail.push({
      x: lerp(fromX, toX, tp),
      y: lerp(fromY, toY, tp),
      a: (1 - i / tailCount) * 0.7,
      s: size * (1 - (i / tailCount) * 0.5),
    });
  }

  return (
    <>
      {tail.map((t, i) => (
        <div key={i} style={{
          position: 'absolute', left: t.x, top: t.y,
          width: t.s, height: t.s,
          marginLeft: -t.s / 2, marginTop: -t.s / 2,
          borderRadius: t.s, background: color, opacity: t.a,
          filter: `blur(${i * 0.5}px)`,
        }} />
      ))}
      <div style={{
        position: 'absolute', left: x, top: y,
        width: size, height: size,
        marginLeft: -size / 2, marginTop: -size / 2,
        borderRadius: size, background: color,
        boxShadow: `0 0 ${size * 3}px ${color}, 0 0 ${size}px ${color}`,
      }} />
      {label && (
        <div style={{
          position: 'absolute', left: x + size, top: y - 18,
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 10, color, letterSpacing: '0.12em', whiteSpace: 'nowrap',
          background: 'rgba(6,8,12,0.7)', padding: '2px 6px',
        }}>{label}</div>
      )}
    </>
  );
}

function PropagationRay({
  fromX, fromY, toX, toY, headProgress, tailProgress, color, strokeWidth = 2,
}: { fromX: number; fromY: number; toX: number; toY: number; headProgress: number; tailProgress: number; color: string; strokeWidth?: number }) {
  const hp = clamp01(headProgress);
  const tp = clamp01(tailProgress);
  if (hp <= 0 || tp >= 1 || hp <= tp) return null;
  if (![fromX, fromY, toX, toY].every(Number.isFinite)) return null;

  const hx = lerp(fromX, toX, hp);
  const hy = lerp(fromY, toY, hp);
  const tx = lerp(fromX, toX, tp);
  const ty = lerp(fromY, toY, tp);

  return (
    <line x1={tx} y1={ty} x2={hx} y2={hy}
      stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
  );
}

function DirectionArrow({
  fromX, fromY, toX, toY, progress, color, label,
}: { fromX: number; fromY: number; toX: number; toY: number; progress: number; color: string; label: string }) {
  const p = clamp01(progress);
  const x = lerp(fromX, toX, p);
  const y = lerp(fromY, toY, p);
  const angle = (Math.atan2(toY - fromY, toX - fromX) * 180) / Math.PI;
  const perpX = -Math.sin((angle * Math.PI) / 180) * 20;
  const perpY = Math.cos((angle * Math.PI) / 180) * 20;
  return (
    <g>
      <g transform={`translate(${x}, ${y}) rotate(${angle})`}>
        <path d="M -12 -5 L 0 0 L -12 5" fill="none" stroke={color}
          strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.7" />
      </g>
      <text x={x + perpX} y={y + perpY}
        fontFamily="JetBrains Mono, monospace" fontSize="10"
        fill={color} letterSpacing="0.15em" textAnchor="middle" opacity="0.85">{label}</text>
    </g>
  );
}

function SignalMeter({
  viewW, y, mode, strength, label, lost = false,
}: { viewW: number; y: number; mode: 'classical' | 'quantum'; strength: number; label: string; lost?: boolean }) {
  const accent = mode === 'classical' ? 'oklch(82% 0.14 75)' : 'oklch(78% 0.14 260)';
  const narrow = viewW < 760;
  const width = narrow ? 140 : 220;
  const padding = narrow ? 8 : 12;
  const barH = narrow ? 18 : 28;
  const left = narrow ? viewW - width - 12 : viewW - 240;
  const top = narrow ? 8 : y;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 80);
    return () => window.clearInterval(id);
  }, []);
  const bars = 20;
  const barArr: number[] = [];
  for (let i = 0; i < bars; i++) {
    let h: number;
    if (lost) h = 0.05 + hash(i * 3.1 + tick * 0.1) * 0.15;
    else h = strength * (0.3 + 0.7 * Math.abs(Math.sin(i * 0.5 + tick / 4)));
    barArr.push(Math.max(0.05, h));
  }
  return (
    <div style={{
      position: 'absolute', left, top, width, padding,
      background: 'rgba(10,12,18,0.75)', backdropFilter: 'blur(8px)',
      border: `1px solid ${mode === 'classical' ? 'rgba(230,180,90,0.3)' : 'rgba(170,150,230,0.3)'}`,
      fontFamily: 'JetBrains Mono, monospace', pointerEvents: 'none',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: narrow ? 5 : 7 }}>
        <span style={{ fontSize: 9, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.55)' }}>{label}</span>
        <span style={{ fontSize: 9, letterSpacing: '0.12em', color: lost ? 'oklch(70% 0.2 25)' : accent }}>
          {lost ? 'NO LOCK' : `${Math.round(strength * 100)}%`}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: barH }}>
        {barArr.map((h, i) => (
          <div key={i} style={{
            flex: 1, height: `${h * 100}%`,
            background: lost ? 'rgba(255,120,100,0.5)' : accent,
            opacity: lost ? 0.6 : 0.85,
          }} />
        ))}
      </div>
    </div>
  );
}

function PhaseTag({ phase, lost = false, done = false }: { phase: string; lost?: boolean; done?: boolean }) {
  const color = lost ? 'oklch(75% 0.2 25)' : done ? 'oklch(85% 0.14 160)' : 'rgba(255,255,255,0.7)';
  return (
    <div className="qr-phase-tag" style={{
      position: 'absolute', bottom: 40, left: 40,
      fontFamily: 'JetBrains Mono, monospace', fontSize: 11, letterSpacing: '0.3em',
      color, padding: '6px 12px', border: `1px solid ${color}`,
      background: 'rgba(6,8,12,0.7)', backdropFilter: 'blur(8px)', pointerEvents: 'none',
    }}>PHASE · {phase}</div>
  );
}

function PhotonDot({
  x, y, color, size = 10, label, pulse = false,
}: { x: number; y: number; color: string; size?: number; label?: string | null; pulse?: boolean }) {
  const scale = pulse ? 1 + 0.2 * Math.sin(Date.now() / 150) : 1;
  return (
    <>
      <div style={{
        position: 'absolute', left: x, top: y,
        width: size, height: size,
        marginLeft: -size / 2, marginTop: -size / 2,
        borderRadius: size, background: color,
        transform: `scale(${scale})`,
        boxShadow: `0 0 ${size * 3}px ${color}, 0 0 ${size}px ${color}`,
        pointerEvents: 'none',
      }} />
      {label && (
        <div style={{
          position: 'absolute', left: x + size + 4, top: y - 8,
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 10, color, letterSpacing: '0.15em', pointerEvents: 'none',
        }}>{label}</div>
      )}
    </>
  );
}

function DetectionBlip({
  x, y, color, label, opacity = 1,
}: { x: number; y: number; color: string; label: string; opacity?: number }) {
  const scale = 0.9 + 0.1 * Math.sin(Date.now() / 150);
  return (
    <div style={{
      position: 'absolute', left: x, top: y,
      transform: `translate(-50%, -50%) scale(${scale})`,
      opacity, pointerEvents: 'none',
    }}>
      <div style={{ width: 26, height: 26, borderRadius: 26, border: `2px solid ${color}` }} />
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: 4, height: 4, marginLeft: -2, marginTop: -2,
        borderRadius: 4, background: color,
      }} />
      <div style={{
        position: 'absolute', left: 32, top: -2,
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 10, color, letterSpacing: '0.12em', whiteSpace: 'nowrap',
      }}>{label}</div>
    </div>
  );
}

function Legend({ color, text }: { color: string; text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
      <div style={{ width: 8, height: 8, borderRadius: 8, background: color, boxShadow: `0 0 8px ${color}`, flexShrink: 0 }} />
      <span>{text}</span>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SCENES
// ═══════════════════════════════════════════════════════════════════════

type SceneProps = { viewW: number; viewH: number };

function Scene1({ viewW, viewH }: SceneProps) {
  const { stepProgress } = useContext(ChapterContext);
  const { t } = useTranslation();
  const [s1, s2, s3] = stepProgress;

  const isNarrow = viewW < 760;
  const stationX = isNarrow ? viewW * 0.22 : viewW * 0.18;
  const stationY = viewH * 0.78;
  const dishY = stationY - 50;
  const planeX = isNarrow ? viewW * 0.78 : viewW * 0.75;
  const planeY = viewH * 0.32;
  const COLOR = 'oklch(82% 0.14 75)';

  const atTarget = s2 > 0.02;
  const impactFlash = s2 < 0.5 ? easeOut(s2 * 2) : easeOut(1 - (s2 - 0.5) * 2);
  const returnHead = s3;
  const returnTail = Math.max(0, s3 - 0.25);
  const detected = s3 > 0.7;

  return (
    <>
      <GridBG mode="classical" />
      <Station x={stationX} y={stationY} mode="classical"
        label={t('quantumRadar.scene1.stationLabel')}
        sub={t('quantumRadar.scene1.stationSub')}
        emitGlow={s1 < 0.3 ? (0.3 - s1) / 0.3 : 0} />
      <Plane x={planeX} y={planeY} stealth={false} />

      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
        <PropagationRay fromX={stationX} fromY={dishY} toX={planeX} toY={planeY}
          headProgress={s1 < 1 ? s1 : 1}
          tailProgress={s2 > 0 ? 1 : Math.max(0, s1 - 0.25)}
          color={COLOR} strokeWidth={2} />

        {s2 > 0 && s2 < 1 && (
          <line x1={stationX} y1={dishY} x2={planeX} y2={planeY}
            stroke={COLOR} strokeWidth="1.5" opacity={0.4} strokeDasharray="2 5" />
        )}

        <PropagationRay fromX={planeX} fromY={planeY} toX={stationX} toY={dishY}
          headProgress={returnHead} tailProgress={returnTail} color={COLOR} strokeWidth={2} />

        {detected && (
          <line x1={stationX} y1={dishY} x2={planeX} y2={planeY}
            stroke={COLOR} strokeWidth="1" strokeDasharray="2 4" opacity="0.5" />
        )}

        {s1 > 0.1 && s1 < 1 && (
          <DirectionArrow fromX={stationX} fromY={dishY} toX={planeX} toY={planeY}
            progress={s1} color={COLOR} label={t('quantumRadar.labels.outgoing')} />
        )}
        {returnHead > 0.1 && returnHead < 1 && (
          <DirectionArrow fromX={planeX} fromY={planeY} toX={stationX} toY={dishY}
            progress={returnHead} color={COLOR} label={t('quantumRadar.labels.echo')} />
        )}
      </svg>

      <SignalPacket fromX={stationX} fromY={dishY} toX={planeX} toY={planeY}
        progress={s1} color={COLOR} size={12} />
      <SignalPacket fromX={planeX} fromY={planeY} toX={stationX} toY={dishY}
        progress={s3} color={COLOR} size={12} />

      {atTarget && (
        <div style={{
          position: 'absolute', left: planeX, top: planeY,
          width: 80 + impactFlash * 60, height: 80 + impactFlash * 60,
          marginLeft: -(40 + impactFlash * 30), marginTop: -(40 + impactFlash * 30),
          borderRadius: '50%',
          background: `radial-gradient(circle, ${COLOR} 0%, transparent 60%)`,
          opacity: impactFlash * 0.5, pointerEvents: 'none',
        }} />
      )}

      {detected && (
        <DetectionBlip x={planeX} y={planeY} color={COLOR}
          label={t('quantumRadar.labels.targetLocked')}
          opacity={(s3 - 0.7) / 0.3} />
      )}

      <SignalMeter viewW={viewW} y={80} mode="classical" strength={s3 * 0.9}
        label={t('quantumRadar.labels.return')} />

      <PhaseTag phase={
        s1 < 1 ? t('quantumRadar.phases.out')
          : s2 < 1 ? t('quantumRadar.phases.impact')
            : t('quantumRadar.phases.returnPhase')}
        done={detected} />
    </>
  );
}

function useNoisePhotons(viewW: number, viewH: number, stationX: number, dishY: number, count = 14, seed = 1) {
  return useMemo(() => {
    const arr: { ox: number; oy: number; tOffset: number; key: number }[] = [];
    for (let i = 0; i < count; i++) {
      const ang = (hash(i * 1.71 + seed) * 2 - 1) * Math.PI * 0.9 - Math.PI / 2;
      const dist = Math.hypot(viewW, viewH) * (0.45 + hash(i * 3.17 + seed) * 0.3);
      const ox = stationX + Math.cos(ang) * dist;
      const oy = dishY + Math.sin(ang) * dist * 0.7;
      const tOffset = hash(i * 5.71 + seed);
      arr.push({ ox, oy, tOffset, key: i });
    }
    return arr;
  }, [viewW, viewH, stationX, dishY, count, seed]);
}

function ReceiverCluster({
  x, y, dots, fade = 1,
}: {
  x: number; y: number; fade?: number;
  dots: { x: number; y: number; size: number; color: string; mark: string | null }[];
}) {
  return (
    <div style={{ position: 'absolute', left: x, top: y, pointerEvents: 'none', opacity: fade }}>
      {dots.map((d, i) => (
        <div key={i} style={{
          position: 'absolute', left: d.x, top: d.y,
          width: d.size, height: d.size,
          marginLeft: -d.size / 2, marginTop: -d.size / 2,
          borderRadius: d.size, background: d.color,
          boxShadow: d.mark === 'match' ? '0 0 14px oklch(85% 0.15 160)' : 'none',
          outline: d.mark === 'match' ? '1px solid oklch(85% 0.15 160)' : 'none',
          outlineOffset: d.mark === 'match' ? '3px' : '0',
        }} />
      ))}
    </div>
  );
}

function Scene2({ viewW, viewH }: SceneProps) {
  const { stepProgress } = useContext(ChapterContext);
  const { t } = useTranslation();
  const [s1, s2, s3] = stepProgress;

  const isNarrow = viewW < 760;
  const stationX = isNarrow ? viewW * 0.22 : viewW * 0.18;
  const stationY = viewH * 0.78;
  const dishY = stationY - 50;
  const planeX = isNarrow ? viewW * 0.78 : viewW * 0.75;
  const planeY = viewH * 0.32;
  const COLOR = 'oklch(82% 0.14 75)';

  const noise = useNoisePhotons(viewW, viewH, stationX, dishY, 16, 1);

  const [tick, setTick] = useState(0);
  useEffect(() => {
    let raf: number;
    const loop = () => { setTick((t) => t + 1); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const echoP = s2;
  const echoActive = s2 > 0.05 && s2 < 1;

  return (
    <>
      <GridBG mode="classical" />
      <Station x={stationX} y={stationY} mode="classical"
        label={t('quantumRadar.scene1.stationLabel')}
        sub={t('quantumRadar.scene2.stationSub')}
        emitGlow={s1 < 0.3 ? (0.3 - s1) / 0.3 : 0} />
      <Plane x={planeX} y={planeY} stealth />

      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
        <PropagationRay fromX={stationX} fromY={dishY} toX={planeX} toY={planeY}
          headProgress={s1 < 1 ? s1 : 1}
          tailProgress={s2 > 0 ? 1 : Math.max(0, s1 - 0.25)}
          color={COLOR} strokeWidth={2} />
        {s1 > 0.1 && s1 < 1 && (
          <DirectionArrow fromX={stationX} fromY={dishY} toX={planeX} toY={planeY}
            progress={s1} color={COLOR} label={t('quantumRadar.labels.outgoing')} />
        )}
        {echoActive && (
          <PropagationRay fromX={planeX} fromY={planeY} toX={stationX} toY={dishY}
            headProgress={echoP} tailProgress={Math.max(0, echoP - 0.25)}
            color={COLOR} strokeWidth={1} />
        )}
      </svg>

      {s1 > 0 && s1 < 1 && (
        <SignalPacket fromX={stationX} fromY={dishY} toX={planeX} toY={planeY}
          progress={s1} color={COLOR} size={11} />
      )}

      {s2 > 0 && s2 < 0.2 && (
        <div style={{
          position: 'absolute', left: planeX, top: planeY,
          width: 40, height: 40, marginLeft: -20, marginTop: -20,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${COLOR} 0%, transparent 60%)`,
          opacity: (1 - s2 / 0.2) * 0.35, pointerEvents: 'none',
        }} />
      )}

      {echoActive && (
        <SignalPacket fromX={planeX} fromY={planeY} toX={stationX} toY={dishY}
          progress={echoP} color={COLOR} size={7} />
      )}

      {(s2 > 0.05 || s3 > 0) && noise.map((n) => {
        const cycle = (tick * 0.012 + n.tOffset) % 1;
        return (
          <SignalPacket key={'n' + n.key}
            fromX={n.ox} fromY={n.oy} toX={stationX} toY={dishY}
            progress={cycle} color="rgba(220, 210, 190, 0.75)" size={6} />
        );
      })}

      {s3 > 0.15 && (
        <ReceiverCluster x={stationX} y={dishY}
          fade={clamp01((s3 - 0.15) / 0.3)}
          dots={(() => {
            const pts: { x: number; y: number; size: number; color: string; mark: string | null }[] = [];
            for (let i = 0; i < 12; i++) {
              const a = hash(i * 2.1 + 9) * Math.PI * 2;
              const r = 30 + hash(i * 1.7 + 9) * 90;
              pts.push({ x: Math.cos(a) * r, y: Math.sin(a) * r - 20, color: 'rgba(220, 210, 190, 0.8)', size: 6, mark: null });
            }
            pts.push({ x: 45, y: -55, color: 'rgba(220, 210, 190, 0.8)', size: 6, mark: null });
            return pts;
          })()} />
      )}

      {s3 > 0.35 && (
        <div style={{
          position: 'absolute',
          left: stationX, top: dishY - 180,
          transform: 'translate(-50%, 0)',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 11, letterSpacing: '0.22em',
          color: 'oklch(75% 0.18 55)',
          padding: '8px 14px',
          border: '1px solid oklch(75% 0.18 55)',
          background: 'rgba(6,8,12,0.85)',
          opacity: clamp01((s3 - 0.35) / 0.2),
          pointerEvents: 'none', whiteSpace: 'nowrap',
        }}>{t('quantumRadar.scene2.realOrNoise')}</div>
      )}

      <SignalMeter viewW={viewW} y={80} mode="classical"
        strength={s2 > 0.3 ? 0.12 + Math.sin(tick / 6) * 0.08 : 0.04 + Math.sin(tick / 8) * 0.02}
        label={t('quantumRadar.labels.return')} lost={s3 > 0.35} />

      <PhaseTag phase={
        s1 < 1 ? t('quantumRadar.phases.out')
          : s2 < 1 ? t('quantumRadar.phases.weakEchoNoise')
            : t('quantumRadar.phases.ambiguous')}
        lost={s3 > 0.35} />
    </>
  );
}

function VerificationCluster({
  stationX, stationY, idlerX, idlerY, progress, sigColor, matchColor, matchLabel,
}: {
  stationX: number; stationY: number; idlerX: number; idlerY: number;
  progress: number; sigColor: string; matchColor: string; matchLabel: string;
}) {
  const dots = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 12; i++) {
      const a = hash(i * 2.1 + 9) * Math.PI * 2;
      const r = 40 + hash(i * 1.7 + 9) * 70;
      arr.push({
        x: stationX + Math.cos(a) * r,
        y: stationY - 20 + Math.sin(a) * r * 0.7,
        isMatch: i === 7,
        delay: i * 0.07,
      });
    }
    return arr;
  }, [stationX, stationY]);

  return (
    <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
      {dots.map((d, i) => {
        const checkP = clamp01((progress - d.delay) / 0.12);
        const doneP = clamp01((progress - d.delay - 0.12) / 0.12);
        const appeared = progress > d.delay * 0.3;
        if (!appeared) return null;
        const threadActive = checkP > 0 && checkP < 1;
        const checked = doneP > 0;
        const color = checked
          ? (d.isMatch ? matchColor : 'rgba(220, 210, 190, 0.25)')
          : 'rgba(220, 210, 190, 0.85)';
        const size = checked ? (d.isMatch ? 9 : 4) : 6;

        return (
          <g key={i}>
            {threadActive && (
              <line x1={idlerX} y1={idlerY} x2={d.x} y2={d.y}
                stroke={d.isMatch ? matchColor : sigColor}
                strokeWidth={d.isMatch ? 1.4 : 0.8}
                strokeDasharray="2 3" opacity={d.isMatch ? 0.8 : 0.35} />
            )}
            {checked && d.isMatch && (
              <line x1={idlerX} y1={idlerY} x2={d.x} y2={d.y}
                stroke={matchColor} strokeWidth="1.2"
                opacity="0.7" strokeDasharray="1 2" />
            )}
            <circle cx={d.x} cy={d.y} r={size / 2} fill={color}
              style={{ filter: (checked && d.isMatch) ? `drop-shadow(0 0 10px ${matchColor})` : 'none' }} />
            {checked && !d.isMatch && (
              <g transform={`translate(${d.x}, ${d.y})`} opacity="0.5">
                <line x1="-3" y1="-3" x2="3" y2="3" stroke="rgba(255,120,110,0.7)" strokeWidth="1" />
                <line x1="-3" y1="3" x2="3" y2="-3" stroke="rgba(255,120,110,0.7)" strokeWidth="1" />
              </g>
            )}
            {checked && d.isMatch && (
              <text x={d.x + 14} y={d.y - 10}
                fontFamily="JetBrains Mono, monospace" fontSize="10"
                fill={matchColor} letterSpacing="0.15em">{matchLabel}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function Scene3({ viewW, viewH }: SceneProps) {
  const { stepProgress, progress } = useContext(ChapterContext);
  const { t } = useTranslation();
  const [s1, s2, s3] = stepProgress;

  const isNarrow = viewW < 760;
  const stationX = isNarrow ? viewW * 0.22 : viewW * 0.18;
  const stationY = viewH * 0.78;
  const dishY = stationY - 50;
  const planeX = isNarrow ? viewW * 0.78 : viewW * 0.72;
  const planeY = viewH * 0.32;

  const SIG_COLOR = 'oklch(78% 0.14 260)';
  const IDLER_COLOR = 'oklch(80% 0.13 200)';
  const MATCH_COLOR = 'oklch(85% 0.17 160)';
  const THREAD_COLOR = 'oklch(85% 0.12 230)';

  const idlerX = stationX + 35;
  const idlerY = dishY - 10 + Math.sin(progress * 4) * 3;

  const noise = useNoisePhotons(viewW, viewH, stationX, dishY, 16, 1);

  const [tick, setTick] = useState(0);
  useEffect(() => {
    let raf: number;
    const loop = () => { setTick((t) => t + 1); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const sigOutP = clamp01(s2 * 2);
  const echoReturnP = clamp01((s2 - 0.5) * 2);

  return (
    <>
      <GridBG mode="quantum" />
      <Station x={stationX} y={stationY} mode="quantum"
        label={t('quantumRadar.scene3.stationLabel')}
        sub={t('quantumRadar.scene3.stationSub')}
        emitGlow={s1 * 0.8} />
      <Plane x={planeX} y={planeY} stealth glow={s3 > 0.5 ? (s3 - 0.5) * 1.4 : 0} />

      {s1 > 0 && s1 < 0.6 && (
        <div style={{
          position: 'absolute', left: idlerX, top: idlerY,
          width: 60 + s1 * 80, height: 60 + s1 * 80,
          marginLeft: -(30 + s1 * 40), marginTop: -(30 + s1 * 40),
          borderRadius: '50%',
          background: `radial-gradient(circle, ${THREAD_COLOR} 0%, transparent 65%)`,
          opacity: (1 - s1 / 0.6) * 0.45, pointerEvents: 'none',
        }} />
      )}

      {s1 > 0.15 && (
        <PhotonDot x={idlerX} y={idlerY} color={IDLER_COLOR} size={11}
          label={s1 < 1 && s1 > 0.4 ? t('quantumRadar.scene3.idlerKept') : null}
          pulse={s3 > 0.4} />
      )}

      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'visible' }}>
        <PropagationRay fromX={idlerX + 20} fromY={idlerY} toX={planeX} toY={planeY}
          headProgress={sigOutP}
          tailProgress={echoReturnP > 0 ? 1 : Math.max(0, sigOutP - 0.25)}
          color={SIG_COLOR} strokeWidth={1.8} />

        {echoReturnP > 0 && (
          <PropagationRay fromX={planeX} fromY={planeY} toX={stationX} toY={dishY}
            headProgress={echoReturnP} tailProgress={Math.max(0, echoReturnP - 0.25)}
            color={SIG_COLOR} strokeWidth={1} />
        )}

        {sigOutP > 0.1 && sigOutP < 0.9 && (
          <DirectionArrow fromX={idlerX + 20} fromY={idlerY} toX={planeX} toY={planeY}
            progress={sigOutP} color={SIG_COLOR} label={t('quantumRadar.labels.signal')} />
        )}
      </svg>

      {sigOutP > 0 && sigOutP < 1 && (
        <SignalPacket fromX={idlerX + 20} fromY={idlerY} toX={planeX} toY={planeY}
          progress={sigOutP} color={SIG_COLOR} size={10} />
      )}

      {echoReturnP > 0 && echoReturnP < 1 && (
        <SignalPacket fromX={planeX} fromY={planeY} toX={stationX} toY={dishY}
          progress={echoReturnP} color={SIG_COLOR} size={7} />
      )}

      {sigOutP > 0.9 && sigOutP < 1 && (
        <div style={{
          position: 'absolute', left: planeX, top: planeY,
          width: 50, height: 50, marginLeft: -25, marginTop: -25,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${SIG_COLOR} 0%, transparent 65%)`,
          opacity: ((sigOutP - 0.9) / 0.1) * 0.5, pointerEvents: 'none',
        }} />
      )}

      {(s2 > 0.3 || s3 > 0) && noise.map((n) => {
        const cycle = (tick * 0.012 + n.tOffset) % 1;
        return (
          <SignalPacket key={'n' + n.key}
            fromX={n.ox} fromY={n.oy} toX={stationX} toY={dishY}
            progress={cycle} color="rgba(220, 210, 190, 0.7)" size={6} />
        );
      })}

      {s3 > 0.1 && (
        <VerificationCluster
          stationX={stationX} stationY={dishY}
          idlerX={idlerX} idlerY={idlerY}
          progress={clamp01((s3 - 0.1) / 0.85)}
          sigColor={SIG_COLOR}
          matchColor={MATCH_COLOR}
          matchLabel={t('quantumRadar.labels.match')} />
      )}

      {s3 > 0.75 && (
        <div style={{
          position: 'absolute', left: idlerX, top: idlerY,
          width: 140, height: 140, marginLeft: -70, marginTop: -70,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${MATCH_COLOR} 0%, transparent 70%)`,
          opacity: ((s3 - 0.75) / 0.25) * 0.35, pointerEvents: 'none',
        }} />
      )}

      {s3 > 0.85 && (
        <DetectionBlip x={planeX} y={planeY} color={MATCH_COLOR}
          label={t('quantumRadar.labels.verifiedOurs')}
          opacity={(s3 - 0.85) / 0.15} />
      )}

      {s1 > 0.1 && (
        <div style={{
          position: 'absolute',
          ...(viewW < 760
            ? { left: 12, bottom: 180, minWidth: 0, maxWidth: 'calc(100vw - 24px)' }
            : { left: 40, top: 80, minWidth: 220 }),
          padding: 12,
          background: 'rgba(10,12,18,0.78)', backdropFilter: 'blur(8px)',
          border: '1px solid rgba(170,150,230,0.3)',
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 10, color: 'rgba(255,255,255,0.85)',
          letterSpacing: '0.08em',
          opacity: clamp01(s1 * 1.5), pointerEvents: 'none',
        }}>
          <div style={{ fontSize: 9, letterSpacing: '0.22em', color: 'rgba(255,255,255,0.5)', marginBottom: 10 }}>
            {t('quantumRadar.scene3.legendTitle')}
          </div>
          <Legend color={IDLER_COLOR} text={t('quantumRadar.scene3.legendIdler')} />
          <Legend color={SIG_COLOR} text={t('quantumRadar.scene3.legendSignal')} />
          <Legend color="rgba(220, 210, 190, 0.8)" text={t('quantumRadar.scene3.legendNoise')} />
          <Legend color={MATCH_COLOR} text={t('quantumRadar.scene3.legendMatch')} />
        </div>
      )}

      <SignalMeter viewW={viewW} y={80} mode="quantum"
        strength={s3 > 0.3 ? 0.25 + s3 * 0.65 : s2 * 0.2}
        label={s3 > 0.3 ? t('quantumRadar.labels.verified') : t('quantumRadar.labels.correlation')} />

      <PhaseTag phase={
        s1 < 1 ? t('quantumRadar.phases.pairBorn')
          : s2 < 1 ? t('quantumRadar.phases.sameWeakEcho')
            : t('quantumRadar.phases.verifyMatch')}
        done={s3 > 0.9} />
    </>
  );
}

function ReceiverPanel({
  x, y, w, h, dots, accent, matchColor, title, question, answer,
  dotsAppeared, mode, classicalTrying = 0, quantumVerifying = 0, tick, idlerLabel,
}: {
  x: number; y: number; w: number; h: number;
  dots: { key: number; x: number; y: number; isEcho: boolean }[];
  accent: string; matchColor?: string;
  title: string; question: string; answer: string;
  dotsAppeared: number; mode: 'classical' | 'quantum';
  classicalTrying?: number; quantumVerifying?: number; tick: number;
  idlerLabel: string;
}) {
  return (
    <div style={{
      position: 'absolute', left: x, top: y, width: w, height: h,
      border: `1px solid ${accent}`, background: 'rgba(6,8,12,0.5)', overflow: 'hidden',
    }}>
      <div style={{
        padding: '14px 16px 10px', borderBottom: `1px solid ${accent}`,
        fontFamily: 'JetBrains Mono, monospace',
      }}>
        <div style={{ fontSize: 10, letterSpacing: '0.3em', color: accent, marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.02em' }}>
          {question}
        </div>
      </div>

      <div style={{ position: 'relative', width: '100%', height: 'calc(100% - 120px)' }}>
        {dots.map((d) => {
          const px = d.x * w;
          const py = d.y * (h - 120);
          let color: string, size: number, glow: string, opacity: number;
          if (mode === 'classical') {
            const flicker = 0.5 + Math.sin(tick * 0.05 + d.key * 1.3) * 0.25;
            color = 'rgba(220, 210, 190, 0.85)';
            size = 5;
            glow = classicalTrying > 0.3 ? `0 0 6px ${accent}` : 'none';
            opacity = dotsAppeared * (0.4 + flicker * 0.5);
          } else {
            const checkOrder = ((d.key * 13) % 60) / 60;
            const checkP = clamp01((quantumVerifying - checkOrder * 0.9) / 0.08);
            const checked = checkP >= 1;
            if (!checked) {
              color = 'rgba(220, 210, 190, 0.85)'; size = 5; glow = 'none'; opacity = dotsAppeared * 0.75;
            } else if (d.isEcho) {
              color = matchColor!; size = 12; glow = `0 0 16px ${matchColor}, 0 0 6px ${matchColor}`; opacity = 1;
            } else {
              color = 'rgba(220, 210, 190, 0.15)'; size = 3; glow = 'none'; opacity = 0.25;
            }
          }
          const style: CSSProperties = {
            position: 'absolute', left: px, top: py,
            width: size, height: size,
            marginLeft: -size / 2, marginTop: -size / 2,
            borderRadius: size, background: color, boxShadow: glow, opacity,
            transition: mode === 'quantum' ? 'all 0.3s' : 'none',
          };
          return <div key={d.key} style={style} />;
        })}

        {mode === 'classical' && classicalTrying > 0.6 && (
          <div style={{
            position: 'absolute', inset: '30% 15%',
            border: '1px dashed rgba(255,180,100,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11, letterSpacing: '0.2em',
            color: 'oklch(75% 0.18 55)',
            opacity: clamp01((classicalTrying - 0.6) / 0.3),
          }}>? ? ?</div>
        )}

        {mode === 'quantum' && quantumVerifying > 0.3 && (() => {
          const echo = dots.find((d) => d.isEcho);
          if (!echo) return null;
          return (
            <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              <line x1={10} y1={h - 140}
                x2={echo.x * w} y2={echo.y * (h - 120)}
                stroke={matchColor} strokeWidth="1" strokeDasharray="2 3"
                opacity={clamp01((quantumVerifying - 0.3) / 0.2) * 0.6} />
            </svg>
          );
        })()}

        {mode === 'quantum' && (
          <div style={{
            position: 'absolute', bottom: 12, left: 12,
            display: 'flex', alignItems: 'center', gap: 8,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 9, letterSpacing: '0.2em',
            color: 'rgba(255,255,255,0.6)',
          }}>
            <div style={{
              width: 10, height: 10, borderRadius: 10,
              background: 'oklch(80% 0.13 200)',
              boxShadow: '0 0 10px oklch(80% 0.13 200)',
            }} />
            <span>{idlerLabel}</span>
          </div>
        )}
      </div>

      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        padding: '12px 16px', borderTop: `1px solid ${accent}`,
        fontFamily: 'JetBrains Mono, monospace',
        background: 'rgba(6,8,12,0.7)',
      }}>
        <div style={{ fontSize: 9, letterSpacing: '0.25em', color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>
          {/* ANSWER label hard-coded — comes through from caller as needed */}
        </div>
        <div style={{
          fontSize: 12, letterSpacing: '0.05em',
          color: (mode === 'quantum' && quantumVerifying > 0.6) ? matchColor
            : (mode === 'classical' && classicalTrying > 0.5 ? 'oklch(75% 0.18 55)' : 'rgba(255,255,255,0.7)'),
        }}>{answer}</div>
      </div>
    </div>
  );
}

function Scene4({ viewW, viewH }: SceneProps) {
  const { stepProgress } = useContext(ChapterContext);
  const { t } = useTranslation();
  const [s1, s2, s3] = stepProgress;

  const isNarrow = viewW < 900;
  const panelW = isNarrow ? viewW * 0.88 : viewW * 0.42;
  const panelH = isNarrow ? viewH * 0.38 : viewH * 0.64;
  const gap = isNarrow ? 24 : 48;

  const leftX = isNarrow ? (viewW - panelW) / 2 : viewW * 0.05;
  const leftY = isNarrow ? viewH * 0.08 : viewH * 0.16;
  const rightX = isNarrow ? (viewW - panelW) / 2 : viewW - panelW - viewW * 0.05;
  const rightY = isNarrow ? leftY + panelH + gap : leftY;

  const [tick, setTick] = useState(0);
  useEffect(() => {
    let raf: number;
    const loop = () => { setTick((t) => t + 1); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const dots = useMemo(() => {
    const arr: { key: number; x: number; y: number; isEcho: boolean }[] = [];
    const N = 60;
    for (let i = 0; i < N; i++) {
      arr.push({
        key: i,
        x: 0.08 + hash(i * 1.3) * 0.84,
        y: 0.15 + hash(i * 2.7) * 0.72,
        isEcho: i === 23,
      });
    }
    return arr;
  }, []);

  const CLASSICAL = 'oklch(82% 0.14 75)';
  const QUANTUM = 'oklch(78% 0.14 260)';
  const MATCH = 'oklch(85% 0.17 160)';

  const dotsAppeared = clamp01(s1 * 1.5);
  const classicalTrying = s2;
  const quantumVerifying = s3;

  return (
    <>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at center, #0c0f17 0%, #05070b 100%)',
      }} />

      <div style={{
        position: 'absolute', top: isNarrow ? 12 : 24, left: 0, right: 0,
        textAlign: 'center', fontFamily: 'JetBrains Mono, monospace',
        fontSize: isNarrow ? 9 : 11, letterSpacing: '0.3em',
        color: 'rgba(255,255,255,0.5)', pointerEvents: 'none',
      }}>{t('quantumRadar.scene4.titleBar')}</div>

      <ReceiverPanel
        x={leftX} y={leftY} w={panelW} h={panelH}
        dots={dots} accent={CLASSICAL}
        title={t('quantumRadar.scene4.classicalTitle')}
        question={t('quantumRadar.scene4.classicalQuestion')}
        answer={classicalTrying > 0.5 ? t('quantumRadar.scene4.classicalAnswer') : '...'}
        dotsAppeared={dotsAppeared} mode="classical"
        classicalTrying={classicalTrying} tick={tick}
        idlerLabel={t('quantumRadar.scene3.legendIdler')} />

      <ReceiverPanel
        x={rightX} y={rightY} w={panelW} h={panelH}
        dots={dots} accent={QUANTUM} matchColor={MATCH}
        title={t('quantumRadar.scene4.quantumTitle')}
        question={t('quantumRadar.scene4.quantumQuestion')}
        answer={quantumVerifying > 0.6 ? t('quantumRadar.scene4.quantumAnswer') : '...'}
        dotsAppeared={dotsAppeared} mode="quantum"
        quantumVerifying={quantumVerifying} tick={tick}
        idlerLabel={t('quantumRadar.scene3.legendIdler')} />

      {s3 > 0.75 && (
        <div style={{
          position: 'absolute', bottom: isNarrow ? 20 : 60,
          left: '50%', transform: 'translateX(-50%)',
          padding: '10px 20px', background: 'rgba(6,8,12,0.9)',
          backdropFilter: 'blur(12px)', border: `1px solid ${MATCH}`,
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: isNarrow ? 11 : 13, letterSpacing: '0.2em',
          color: MATCH, opacity: clamp01((s3 - 0.75) / 0.2),
          pointerEvents: 'none',
        }}>{t('quantumRadar.scene4.banner')}</div>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// CHAPTER + HERO + OUTRO + NAV
// ═══════════════════════════════════════════════════════════════════════

function ChapterSection({ index, chapter, SceneComp }: { index: number; chapter: Chapter; SceneComp: (p: SceneProps) => JSX.Element }) {
  const ref = useRef<HTMLElement>(null);
  const [progress, setProgress] = useState(0);
  const [viewSize, setViewSize] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1200,
    h: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));
  const numSteps = chapter.steps.length;

  useEffect(() => {
    const onScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const totalH = ref.current.offsetHeight;
      const scrolled = -rect.top;
      const maxScroll = totalH - vh;
      const p = maxScroll > 0 ? clamp01(scrolled / maxScroll) : 0;
      setProgress(p);
      setViewSize({ w: window.innerWidth, h: vh });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const titleHold = 0.22;
  const stepSpan = (1 - titleHold) / numSteps;
  const stepProgress = Array.from({ length: numSteps }).map((_, i) => {
    const start = titleHold + i * stepSpan;
    const end = start + stepSpan;
    return clamp01((progress - start) / (end - start));
  });

  return (
    <section ref={ref} style={{ position: 'relative', height: `${(numSteps + 2) * 100}dvh` }}>
      <div style={{ position: 'sticky', top: 0, width: '100%', height: '100dvh', overflow: 'hidden' }}>
        <ChapterContext.Provider value={{ progress, stepProgress }}>
          <SceneComp viewW={viewSize.w} viewH={viewSize.h} />
        </ChapterContext.Provider>
        <ChapterHeader chapter={chapter} progress={progress} />
        <StepOverlay chapter={chapter} stepProgress={stepProgress} progress={progress} />
      </div>
    </section>
  );
}

function ChapterHeader({ chapter, progress }: { chapter: Chapter; progress: number }) {
  const fullAt = 0.05, fadeStart = 0.22, fadeEnd = 0.30;
  let opacity: number;
  if (progress < fullAt) opacity = progress / fullAt;
  else if (progress < fadeStart) opacity = 1;
  else if (progress < fadeEnd) opacity = 1 - ((progress - fadeStart) / (fadeEnd - fadeStart)) * 0.75;
  else opacity = 0.25;

  return (
    <div style={{
      position: 'absolute',
      top: 'clamp(64px, 10vh, 120px)', left: 'clamp(24px, 4vw, 64px)',
      maxWidth: 'min(720px, calc(100vw - 48px))',
      opacity, transition: 'opacity 0.3s', pointerEvents: 'none',
    }}>
      <div style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 12, letterSpacing: '0.3em',
        color: 'rgba(255,255,255,0.5)', marginBottom: 16,
      }}>PART {chapter.number} / 04</div>
      <h2 style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 'clamp(28px, 5vw, 64px)', fontWeight: 300,
        letterSpacing: '-0.02em', lineHeight: 1.05, margin: 0,
        color: '#f2eee5',
      }}>{chapter.title}</h2>
      <p style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 'clamp(14px, 1.6vw, 18px)', fontWeight: 400,
        color: 'rgba(242,238,229,0.72)', lineHeight: 1.5,
        marginTop: 20, maxWidth: 520,
      }}>{chapter.intro}</p>
    </div>
  );
}

function StepOverlay({ chapter, stepProgress, progress }: { chapter: Chapter; stepProgress: number[]; progress: number }) {
  let idx = -1;
  for (let i = 0; i < stepProgress.length; i++) {
    if (stepProgress[i] > 0 && stepProgress[i] < 1) { idx = i; break; }
  }
  if (idx < 0) {
    for (let i = stepProgress.length - 1; i >= 0; i--) {
      if (stepProgress[i] >= 1) { idx = i; break; }
    }
  }
  if (idx < 0 || progress < 0.2) return null;

  const step = chapter.steps[idx];
  const sp = stepProgress[idx];
  const opacity = sp < 0.12 ? sp / 0.12 : sp > 0.9 ? (1 - sp) / 0.1 : 1;

  return (
    <div className="qr-step-overlay" style={{
      position: 'absolute',
      bottom: 'clamp(40px, 8vh, 80px)',
      right: 'clamp(24px, 4vw, 56px)',
      maxWidth: 'min(420px, calc(100vw - 48px))',
      padding: 'clamp(18px, 2.5vw, 26px)',
      background: 'rgba(6, 8, 12, 0.82)',
      backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)',
      border: '1px solid rgba(255,255,255,0.08)',
      opacity: clamp01(opacity), transition: 'opacity 0.15s',
      pointerEvents: 'none',
    }}>
      <div style={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 10, letterSpacing: '0.25em',
        color: 'rgba(255,255,255,0.45)', marginBottom: 10,
      }}>STEP {idx + 1} / {chapter.steps.length}</div>
      <h3 style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 'clamp(17px, 2vw, 22px)', fontWeight: 500,
        letterSpacing: '-0.01em', lineHeight: 1.2, margin: 0,
        color: '#f2eee5',
      }}>{step.heading}</h3>
      <p style={{
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 'clamp(13px, 1.3vw, 15px)', fontWeight: 400,
        color: 'rgba(242,238,229,0.72)', lineHeight: 1.55,
        marginTop: 10, marginBottom: 0,
      }}>{step.body}</p>
    </div>
  );
}

function HeroAmbient() {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf: number;
    const loop = () => { setT(performance.now() / 1000); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  const dots = Array.from({ length: 14 }).map((_, i) => {
    const seed = i * 1.7;
    const x = 50 + Math.sin(t * 0.3 + seed) * 38;
    const y = 50 + Math.cos(t * 0.25 + seed * 1.3) * 32;
    const color = i % 2 === 0 ? 'oklch(78% 0.14 260)' : 'oklch(80% 0.13 200)';
    return { x, y, color, key: i };
  });

  return (
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {dots.map((d) => (
        <div key={d.key} style={{
          position: 'absolute', left: `${d.x}%`, top: `${d.y}%`,
          width: 4, height: 4, marginLeft: -2, marginTop: -2,
          borderRadius: 4, background: d.color,
          boxShadow: `0 0 20px ${d.color}`, opacity: 0.6,
        }} />
      ))}
    </div>
  );
}

function Hero({ kicker, hero, heroSub, scrollHint }: { kicker: string; hero: string; heroSub: string; scrollHint: string }) {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const onScroll = () => setProgress(clamp01(window.scrollY / window.innerHeight));
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section style={{
      position: 'relative', height: '100dvh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      textAlign: 'center', padding: '0 24px',
    }}>
      <HeroAmbient />
      <div style={{
        opacity: 1 - progress,
        transform: `translateY(${progress * 40}px)`,
        maxWidth: 1000, zIndex: 2,
      }}>
        <div style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 'clamp(10px, 1.2vw, 13px)',
          letterSpacing: '0.4em', color: 'oklch(78% 0.14 260)', marginBottom: 32,
        }}>{kicker}</div>
        <h1 style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 'clamp(36px, 8vw, 100px)', fontWeight: 300,
          letterSpacing: '-0.03em', lineHeight: 1.02, margin: 0,
          color: '#f2eee5',
        }}>{hero}</h1>
        <p style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 'clamp(14px, 1.7vw, 19px)', fontWeight: 400,
          color: 'rgba(242,238,229,0.68)', lineHeight: 1.5,
          marginTop: 28, maxWidth: 620, marginLeft: 'auto', marginRight: 'auto',
        }}>{heroSub}</p>
      </div>

      <div style={{
        position: 'absolute', bottom: 40, left: '50%',
        transform: 'translateX(-50%)', opacity: 1 - progress,
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: 10, letterSpacing: '0.3em',
        color: 'rgba(255,255,255,0.55)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
      }}>
        <span>{scrollHint}</span>
        <div style={{
          width: 1, height: 40,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.5) 0%, rgba(255,255,255,0) 100%)',
        }} />
      </div>
    </section>
  );
}

function Outro({ title, body, cta }: { title: string; body: string; cta: string }) {
  return (
    <section style={{
      minHeight: '80dvh',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '100px 24px 64px', textAlign: 'center',
    }}>
      <div style={{ maxWidth: 720 }}>
        <h2 style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 'clamp(28px, 5vw, 52px)', fontWeight: 300,
          letterSpacing: '-0.02em', lineHeight: 1.1, margin: 0,
          color: '#f2eee5',
        }}>{title}</h2>
        <p style={{
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 'clamp(15px, 1.7vw, 19px)',
          color: 'rgba(242,238,229,0.7)', lineHeight: 1.5, marginTop: 24,
        }}>{body}</p>
        <div style={{
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 11, letterSpacing: '0.25em',
          color: 'rgba(255,255,255,0.4)', marginTop: 44,
        }}>{cta}</div>
      </div>
    </section>
  );
}

function ScrollProgress({ chapters }: { chapters: Chapter[] }) {
  const [progress, setProgress] = useState(0);
  const [chapterIdx, setChapterIdx] = useState(-1);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? window.scrollY / total : 0);
      const sections = document.querySelectorAll('[data-chapter]');
      let active = -1;
      sections.forEach((s, i) => {
        const r = s.getBoundingClientRect();
        if (r.top <= window.innerHeight * 0.5 && r.bottom > window.innerHeight * 0.5) active = i;
      });
      setChapterIdx(active);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0,
        height: 2, zIndex: 100, background: 'rgba(255,255,255,0.06)',
      }}>
        <div style={{
          height: '100%', width: `${progress * 100}%`,
          background: 'linear-gradient(90deg, oklch(82% 0.14 75), oklch(78% 0.14 260))',
          transition: 'width 0.1s',
        }} />
      </div>

      <div style={{
        position: 'fixed', left: 20, top: '50%',
        transform: 'translateY(-50%)', zIndex: 100,
        display: 'flex', flexDirection: 'column', gap: 14,
      }}>
        {chapters.map((c, i) => (
          <a key={i} href={`#qr-chapter-${i}`} style={{
            display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none',
          }}>
            <div style={{
              width: chapterIdx === i ? 24 : 14,
              height: 1,
              background: chapterIdx === i ? '#f2eee5' : 'rgba(255,255,255,0.35)',
              transition: 'width 0.3s, background 0.3s',
            }} />
            <span style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 10, letterSpacing: '0.2em',
              color: chapterIdx === i ? '#f2eee5' : 'rgba(255,255,255,0.4)',
              transition: 'color 0.3s',
            }}>{c.number}</span>
          </a>
        ))}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════

export default function QuantumRadar() {
  const { t } = useTranslation();

  // Build chapters from i18n. Each chapter has title, intro, and 3 steps.
  const chapters: Chapter[] = useMemo(() => {
    const result: Chapter[] = [];
    for (let i = 1; i <= 4; i++) {
      const num = String(i).padStart(2, '0');
      const steps: Step[] = [];
      for (let s = 1; s <= 3; s++) {
        steps.push({
          heading: t(`quantumRadar.chapters.ch${i}.steps.s${s}.heading`),
          body: t(`quantumRadar.chapters.ch${i}.steps.s${s}.body`),
        });
      }
      result.push({
        number: num,
        title: t(`quantumRadar.chapters.ch${i}.title`),
        intro: t(`quantumRadar.chapters.ch${i}.intro`),
        steps,
      });
    }
    return result;
  // re-build when language changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const scenes = [Scene1, Scene2, Scene3, Scene4];

  useEffect(() => {
    document.body.classList.add('qr-active');
    return () => document.body.classList.remove('qr-active');
  }, []);

  return (
    <div className="quantum-radar-page" style={{
      background: '#06070a', color: '#f2eee5', minHeight: '100dvh',
    }}>
      <ScrollProgress chapters={chapters} />
      <Hero
        kicker={t('quantumRadar.hero.kicker')}
        hero={t('quantumRadar.hero.title')}
        heroSub={t('quantumRadar.hero.subtitle')}
        scrollHint={t('quantumRadar.hero.scrollHint')} />
      {chapters.map((chapter, i) => (
        <div key={i} id={`qr-chapter-${i}`} data-chapter={i}>
          <ChapterSection index={i} chapter={chapter} SceneComp={scenes[i]} />
        </div>
      ))}
      <Outro
        title={t('quantumRadar.outro.title')}
        body={t('quantumRadar.outro.body')}
        cta={t('quantumRadar.outro.cta')} />
    </div>
  );
}
